import { useState, useEffect } from 'react';

function makeCatUid() {
  return 'lia_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4);
}

function makeSubUid() {
  return 'sub_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4);
}

export default function LiabilityProfileEditor({ liabilityCategories, stages, onSave, saving }) {
  const [localCats, setLocalCats] = useState([]);
  const [expandedUid, setExpandedUid] = useState(null);
  const [newSubInputs, setNewSubInputs] = useState({});

  useEffect(() => {
    setLocalCats(liabilityCategories || []);
  }, [liabilityCategories]);

  function handleCatChange(uid, key, value) {
    setLocalCats(prev => prev.map(c => c.uid === uid ? { ...c, [key]: value } : c));
  }

  function handleAddSub(catUid) {
    const text = (newSubInputs[catUid] || '').trim();
    if (!text) return;
    setLocalCats(prev => prev.map(c => {
      if (c.uid !== catUid) return c;
      const newSub = { uid: makeSubUid(), name: text, order: (c.sub_categories || []).length + 1 };
      return { ...c, sub_categories: [...(c.sub_categories || []), newSub] };
    }));
    setNewSubInputs(prev => ({ ...prev, [catUid]: '' }));
  }

  function handleDeleteSub(catUid, subUid) {
    setLocalCats(prev => prev.map(c => {
      if (c.uid !== catUid) return c;
      return { ...c, sub_categories: c.sub_categories.filter(s => s.uid !== subUid) };
    }));
  }

  function handleAddCategory() {
    const newCat = {
      uid: makeCatUid(),
      category_name: 'New Liability Category',
      icon: '&#128203;',
      sub_categories: [],
      fields: [],
      stage_uid: '',
      order: localCats.length + 1,
    };
    setLocalCats(prev => [...prev, newCat]);
    setExpandedUid(newCat.uid);
  }

  function handleDeleteCategory(uid) {
    if (!window.confirm('Delete this liability category and all its sub-categories?')) return;
    setLocalCats(prev => prev.filter(c => c.uid !== uid));
    if (expandedUid === uid) setExpandedUid(null);
  }

  function handleToggleExpand(uid) {
    setExpandedUid(prev => prev === uid ? null : uid);
  }

  function handleSave() {
    onSave(localCats);
  }

  const sorted = [...localCats].sort((a, b) => a.order - b.order);

  return (
    <div>
      <div className="pm__toolbar">
        <span className="pm__section-title">Liability Categories</span>
        <button className="btn btn--outline-primary" onClick={handleAddCategory}>+ Add Category</button>
      </div>

      <div className="pm__cat-list">
        {sorted.map(cat => (
          <div key={cat.uid} className="pm__cat-card">
            <div className="pm__cat-header" onClick={() => handleToggleExpand(cat.uid)}>
              <div className="pm__cat-title">
                <input
                  className="pm__input"
                  style={{ width: '42px', textAlign: 'center' }}
                  value={cat.icon || ''}
                  onChange={e => { e.stopPropagation(); handleCatChange(cat.uid, 'icon', e.target.value); }}
                  onClick={e => e.stopPropagation()}
                  title="Icon (emoji)"
                />
                <input
                  className="pm__input"
                  style={{ fontWeight: 600, minWidth: '150px' }}
                  value={cat.category_name}
                  onChange={e => { e.stopPropagation(); handleCatChange(cat.uid, 'category_name', e.target.value); }}
                  onClick={e => e.stopPropagation()}
                />
                <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                  {(cat.sub_categories || []).length} sub-categories
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                <select
                  className="pm__select"
                  value={cat.stage_uid || ''}
                  onChange={e => handleCatChange(cat.uid, 'stage_uid', e.target.value)}
                >
                  <option value="">No stage</option>
                  {(stages || []).map(s => (
                    <option key={s.uid} value={s.uid}>{s.icon} {s.name}</option>
                  ))}
                </select>
                <button className="pm__del-btn" onClick={() => handleDeleteCategory(cat.uid)} title="Delete category">
                  &#10005;
                </button>
                <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                  {expandedUid === cat.uid ? '&#9650;' : '&#9660;'}
                </span>
              </div>
            </div>

            {expandedUid === cat.uid && (
              <div className="pm__cat-body">
                <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                  Sub-categories
                </div>
                <div className="pm__sub-list">
                  {(cat.sub_categories || []).map(sub => (
                    <span key={sub.uid} className="pm__sub-chip">
                      {sub.name}
                      <button className="pm__sub-chip-del" onClick={() => handleDeleteSub(cat.uid, sub.uid)}>
                        &#10005;
                      </button>
                    </span>
                  ))}
                </div>
                <div className="pm__add-sub-row">
                  <input
                    className="pm__mini-input"
                    placeholder="New sub-category name"
                    value={newSubInputs[cat.uid] || ''}
                    onChange={e => setNewSubInputs(prev => ({ ...prev, [cat.uid]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddSub(cat.uid); }}
                  />
                  <button
                    className="btn btn--outline"
                    style={{ fontSize: '0.75rem', padding: '5px 10px' }}
                    onClick={() => handleAddSub(cat.uid)}
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {localCats.length === 0 && (
          <div className="pm__empty">No liability categories. Click + Add Category to create one.</div>
        )}
      </div>

      <div className="pm__editor-footer">
        <button
          className={`btn ${saving ? 'btn--disabled' : 'btn--primary'}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
